import React from 'react';
import { PiSquaresFour } from 'react-icons/pi';

import type { TreeDataNode } from 'antd';
import {
  COPY_TOOL_NO_PLANS_GROUP_LABEL,
  UNNAMED_INPUT_LABEL,
} from 'constants/uiConstants';
import { IInput } from 'interfaces/IInput';
import {
  createCropNode,
  createInputNode,
} from 'pages/FarmsPage/Sidebar/FarmTreeViewNodes';
import {
  formatDocName,
  groupInputsByCrop,
  resolvePaddockName,
  sortInputsForCropMode,
} from 'utils/FarmUtils';

import { CopyTreeIndex } from './buildCopyTreeData';

/** Not a crop key, so it can't clash with a crop's group. */
export const COPY_NO_PLANS_GROUP_KEY = 'copy-no-plans-group';

const createNoPlansGroupNode = (children: TreeDataNode[]): TreeDataNode => ({
  key: COPY_NO_PLANS_GROUP_KEY,
  title: (
    <span className="flex min-w-0 flex-1 items-center">
      <PiSquaresFour
        className="mr-1.5 flex-shrink-0 text-gray-muted"
        size={14}
      />
      <span className="truncate">{COPY_TOOL_NO_PLANS_GROUP_LABEL}</span>
      <span className="ml-1 flex items-center text-nowrap text-sm text-gray-muted">
        {`(${children.length})`}
      </span>
    </span>
  ),
  children,
});

interface BuildCopyCropTreeDataParams {
  /** The Farm view tree as shown: already searched and with the Source excluded. */
  farmTreeData: TreeDataNode[];
  /** Holds every node shown, including the Source document on Destination. */
  lookupIndex: CopyTreeIndex;
  paddockNames: Map<string, string>;
  /** Destination: crop rows tick the documents shown under them. */
  allowMultiple: boolean;
  /** Destination lists the paddocks with no documents, which can get a new one. */
  includeEmptyPaddocks: boolean;
  /** Add as new: a document is ticked to add a new one to its paddocks. */
  paddocksOnly: boolean;
}

/**
 * Crop view: crop → documents with that crop, each labelled by its paddock as
 * in the sidebar. A document linked to several paddocks shows once, under the
 * key of its first paddock, so its ticks are shared with Farm view.
 */
export const buildCopyCropTreeData = ({
  farmTreeData,
  lookupIndex,
  paddockNames,
  allowMultiple,
  includeEmptyPaddocks,
  paddocksOnly,
}: BuildCopyCropTreeDataParams): TreeDataNode[] => {
  const nodesById = new Map<string, TreeDataNode>();
  const inputs: IInput[] = [];
  const emptyPaddockNodes: TreeDataNode[] = [];

  farmTreeData.forEach((farm) =>
    (farm.children ?? []).forEach((paddockNode) => {
      const paddock = lookupIndex.get(String(paddockNode.key));
      if (paddock?.kind !== 'paddock') return;
      if (paddock.isEmpty) {
        if (includeEmptyPaddocks) emptyPaddockNodes.push(paddockNode);
        return;
      }
      (paddockNode.children ?? []).forEach((node) => {
        const info = lookupIndex.get(String(node.key));
        if (info?.kind !== 'input' || nodesById.has(info.input.id)) return;
        nodesById.set(info.input.id, node);
        inputs.push(info.input);
      });
    })
  );

  const cropNodes = groupInputsByCrop(inputs).map(
    ({ cropKey, cropName, inputs: cropInputs }) =>
      createCropNode(
        cropKey,
        cropName,
        sortInputsForCropMode(cropInputs, paddockNames).map((input) => {
          const node = nodesById.get(input.id);
          // As the sidebar: the crop is the parent, so a document is labelled by its paddock.
          const label =
            resolvePaddockName(input, paddockNames) ||
            formatDocName(input.docName) ||
            UNNAMED_INPUT_LABEL;
          return {
            ...node,
            key: node?.key ?? input.id,
            title: createInputNode(input, undefined, label).title,
            // Farm view lists Add as new's documents for context only; here they stand for their paddocks.
            ...(paddocksOnly && { checkable: true }),
          };
        })
      )
  );

  return [
    ...cropNodes,
    ...(emptyPaddockNodes.length > 0
      ? [createNoPlansGroupNode(emptyPaddockNodes)]
      : []),
  ].map((node) => ({ ...node, checkable: allowMultiple }));
};

/** Every crop group open, so a switch to Crop view shows the documents. */
export const getCropViewDefaultExpandedKeys = (
  treeData: TreeDataNode[]
): string[] => treeData.map((node) => String(node.key));
